
import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Form } from '@/components/ui/form';
import SiteAddressFields from '@/components/ui/site-address-fields';
import PageLayout from '@/components/layout/PageLayout';
import FormActions from '@/components/shared/FormActions';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

interface ProjectFormProps {
  projectId?: string;
}

interface ProjectFormData {
  name: string;
  description: string;
  status: string;
  expected_start_date: string;
  expected_end_date: string;
  use_separate_site_address: boolean;
  site_street_address: string;
  site_suburb: string;
  site_city: string;
  site_province_state: string;
  site_postal_code: string;
}

const ProjectForm: React.FC<ProjectFormProps> = ({ projectId }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = !!projectId;

  const { data: project, isLoading } = useQuery({
    queryKey: ['project', projectId],
    queryFn: async () => {
      if (!projectId) return null;
      
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('id', projectId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!projectId,
  });

  const form = useForm<ProjectFormData>({
    defaultValues: {
      name: '',
      description: '',
      status: 'planning',
      expected_start_date: '',
      expected_end_date: '',
      use_separate_site_address: false,
      site_street_address: '',
      site_suburb: '',
      site_city: '',
      site_province_state: '',
      site_postal_code: '',
    }
  });

  const { register, handleSubmit, watch, setValue, formState: { errors } } = form;

  // Track form initialization state
  const [isFormInitialized, setIsFormInitialized] = React.useState(!isEdit);

  // Update form when project data loads
  React.useEffect(() => {
    if (project) {
      setValue('name', project.name || '');
      setValue('description', project.description || '');
      setValue('status', project.status || 'planning');
      setValue('expected_start_date', project.expected_start_date || '');
      setValue('expected_end_date', project.expected_end_date || '');
      setValue('use_separate_site_address', project.use_separate_site_address || false);
      setValue('site_street_address', project.site_street_address || '');
      setValue('site_suburb', project.site_suburb || '');
      setValue('site_city', project.site_city || '');
      setValue('site_province_state', project.site_province_state || '');
      setValue('site_postal_code', project.site_postal_code || '');
      
      // Mark form as initialized after all values are set
      setIsFormInitialized(true);
    }
  }, [project, setValue]);

  const useSeparateAddress = watch('use_separate_site_address');

  const updateProjectMutation = useMutation({
    mutationFn: async (data: ProjectFormData) => {
      if (!projectId) throw new Error('No project ID');

      const { error } = await supabase
        .from('projects')
        .update({
          name: data.name,
          description: data.description,
          status: data.status,
          expected_start_date: data.expected_start_date || null,
          expected_end_date: data.expected_end_date || null,
          use_separate_site_address: data.use_separate_site_address,
          site_street_address: data.use_separate_site_address ? data.site_street_address : null,
          site_suburb: data.use_separate_site_address ? data.site_suburb : null,
          site_city: data.use_separate_site_address ? data.site_city : null,
          site_province_state: data.use_separate_site_address ? data.site_province_state : null,
          site_postal_code: data.use_separate_site_address ? data.site_postal_code : null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', projectId);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Project updated successfully');
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      navigate(`/projects/${projectId}`);
    },
    onError: (error) => {
      console.error('Error updating project:', error);
      toast.error('Failed to update project');
    }
  });

  const onSubmit = (data: ProjectFormData) => {
    updateProjectMutation.mutate(data);
  };

  if (isLoading) {
    return (
      <PageLayout maxWidth="md" padding="md">
        <p>Loading project...</p>
      </PageLayout>
    );
  }

  return (
    <PageLayout maxWidth="md" padding="md">
      <Card>
        <CardHeader>
          <CardTitle>{isEdit ? 'Edit Project' : 'Create Project'}</CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name">Project Name</Label>
                <Input
                  id="name"
                  {...register('name', { required: 'Project name is required' })}
                  placeholder="Enter project name"
                />
                {errors.name && (
                  <p className="text-sm text-destructive">{errors.name.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  {...register('description')}
                  placeholder="Enter project description"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <select
                  id="status"
                  {...register('status')}
                  className="w-full px-3 py-2 border border-input bg-background rounded-md"
                >
                  <option value="planning">Planning</option>
                  <option value="pending_start">Pending Start</option>
                  <option value="in_progress">In Progress</option>
                  <option value="on_hold">On Hold</option>
                  <option value="delayed">Delayed</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="expected_start_date">Expected Start Date</Label>
                  <Input
                    id="expected_start_date"
                    type="date"
                    {...register('expected_start_date')}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="expected_end_date">Expected End Date</Label>
                  <Input
                    id="expected_end_date"
                    type="date"
                    {...register('expected_end_date')}
                  />
                </div>
              </div>

              {/* Only render SiteAddressFields when form is fully initialized */}
              {isFormInitialized && <SiteAddressFields form={form} />}

              <FormActions
                onCancel={() => navigate(projectId ? `/projects/${projectId}` : '/projects')}
                isSubmitting={updateProjectMutation.isPending}
                submitLabel={isEdit ? 'Update Project' : 'Create Project'}
                submittingLabel={isEdit ? 'Updating...' : 'Creating...'}
              />
            </form>
          </Form>
        </CardContent>
      </Card>
    </PageLayout>
  );
};

export default ProjectForm;
